import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Button,
  styled,
  TextField,
  InputAdornment,
  Chip,
  TablePagination,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  TableSortLabel,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
} from '@mui/icons-material';
import { useClientContext } from '../../context/ClientContext';
import { Client } from '../../types/client';
import { User } from '../../types/user.types';
import { getRepresentatives } from '../../api/users';
import AddClientDialog from '../../components/Client/AddClientDialog';

// Styled component for clickable row
const StyledTableRow = styled(TableRow)(({ theme }) => ({
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

const Clients: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { clients, loading, error, fetchClients } = useClientContext();
  const [isAddClientOpen, setIsAddClientOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(10);
  const [statusFilter, setStatusFilter] = useState('all');
  const [representatives, setRepresentatives] = useState<User[]>([]);
  const [representativeFilter, setRepresentativeFilter] = useState('all');
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');
  const [orderBy, setOrderBy] = useState<string>('companyName');

  const getStatusColor = (status: string) => {
    const normalizedStatus = status?.toLowerCase();
    switch (normalizedStatus) {
      case 'active':
      case 'active_no_services':
        return 'success';
      case 'loa_issued':
      case 'quote_issued':
        return 'warning';
      case 'loa_received':
      case 'quote_accepted':
        return 'info';
      case 'loa_rejected':
      case 'quote_declined':
        return 'error';
      case 'draft':
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    const normalizedStatus = status?.toLowerCase();
    switch (normalizedStatus) {
      case 'draft': return 'Draft';
      case 'loa_issued': return 'LOA Issued';
      case 'loa_received': return 'LOA Received';
      case 'loa_rejected': return 'LOA Rejected';
      case 'quote_issued': return 'Quote Issued';
      case 'quote_accepted': return 'Quote Accepted';
      case 'quote_declined': return 'Quote Declined';
      case 'active_no_services': return 'Active (No Services)';
      case 'active': return 'Active';
      default: return status;
    }
  };

  // Filter, search, and sort logic
  const filteredClients = useMemo(() => {
    // First filter the clients
    const filtered = clients.filter(client => {
      const searchTermLower = searchTerm.toLowerCase();
      const matchesSearch = (
        client.companyName?.toLowerCase().includes(searchTermLower) ||
        client.ownerName?.toLowerCase().includes(searchTermLower) ||
        client.representativeId?.toString().includes(searchTermLower) ||
        // Trading address postcode
        client.tradingAddress?.postcode?.toLowerCase().includes(searchTermLower) ||
        // Properties postcodes
        client.properties?.some(property => 
          property.address?.postcode?.toLowerCase().includes(searchTermLower)
        )
      );

      const matchesStatus = 
        statusFilter === 'all' || 
        (client.pipelineStatus || client.status)?.toLowerCase() === statusFilter.toLowerCase();

      const matchesRepresentative =
        representativeFilter === 'all' ||
        (typeof client.representativeId === 'string' 
          ? representativeFilter === 'unassigned'
          : (client.representativeId as unknown as User)._id === representativeFilter);

      return matchesSearch && matchesStatus && matchesRepresentative;
    });

    // Then sort the filtered clients
    return [...filtered].sort((a, b) => {
      if (orderBy === 'companyName') {
        return order === 'asc'
          ? (a.companyName || '').localeCompare(b.companyName || '')
          : (b.companyName || '').localeCompare(a.companyName || '');
      } else if (orderBy === 'updatedAt') {
        const dateA = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
        const dateB = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
        return order === 'asc' ? dateA - dateB : dateB - dateA;
      }
      return 0;
    });
  }, [clients, searchTerm, statusFilter, representativeFilter, orderBy, order]);

  // Pagination logic
  const paginatedClients = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return filteredClients.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredClients, page, rowsPerPage]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(0); // Reset to first page when searching
  };

  const handleStatusFilterChange = (event: any) => {
    setStatusFilter(event.target.value);
    setPage(0); // Reset to first page when filtering
  };

  const handleRequestSort = (property: string) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
    setPage(0); // Reset to first page when sorting
  };

  // Initial load with loading state
  useEffect(() => {
    const loadData = async () => {
      await fetchClients();
      // Fetch representatives
      try {
        const reps = await getRepresentatives();
        setRepresentatives(reps);
      } catch (error) {
        console.error('Failed to load representatives:', error);
      }
    };
    loadData();
  }, [fetchClients]);

  // Auto-refresh every 30 seconds (without loading state)
  useEffect(() => {
    const refreshInterval = setInterval(() => {
      fetchClients();
    }, 30000);

    return () => clearInterval(refreshInterval);
  }, [fetchClients]);

  // Handle action=add query parameter to auto-open Add Client dialog
  useEffect(() => {
    const action = searchParams.get('action');
    if (action === 'add') {
      setIsAddClientOpen(true);
      // Remove the action parameter from URL to clean up
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('action');
      setSearchParams(newSearchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const handleAddClientOpen = () => {
    setIsAddClientOpen(true);
  };

  const handleAddClientClose = () => {
    setIsAddClientOpen(false);
    // Refresh the client list after adding a new client
    fetchClients();
  };

  const handleViewClient = (client: Client) => {
    navigate(`/utilities/clients/${client._id}`);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" sx={{ fontWeight: 'bold' }}>Clients</Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleAddClientOpen}
        >
          Add Client
        </Button>
      </Box>

      <Box display="flex" gap={2} mb={3} alignItems="center">
        <TextField
          placeholder="Search clients..."
          variant="outlined"
          size="small"
          fullWidth
          value={searchTerm}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        <FormControl variant="outlined" size="small" sx={{ minWidth: 120, mr: 2 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            onChange={handleStatusFilterChange}
            label="Status"
            startAdornment={<FilterIcon sx={{ mr: 1 }} />}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="draft">Draft</MenuItem>
            <MenuItem value="loa_issued">LOA Issued</MenuItem>
            <MenuItem value="loa_received">LOA Received</MenuItem>
            <MenuItem value="loa_rejected">LOA Rejected</MenuItem>
            <MenuItem value="quote_issued">Quote Issued</MenuItem>
            <MenuItem value="quote_accepted">Quote Accepted</MenuItem>
            <MenuItem value="quote_declined">Quote Declined</MenuItem>
            <MenuItem value="active_no_services">Active (No Services)</MenuItem>
            <MenuItem value="active">Active</MenuItem>
          </Select>
        </FormControl>
        <FormControl variant="outlined" size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Representative</InputLabel>
          <Select
            value={representativeFilter}
            onChange={(e) => {
              setRepresentativeFilter(e.target.value);
              setPage(0); // Reset to first page when filtering
            }}
            label="Representative"
            startAdornment={<FilterIcon sx={{ mr: 1 }} />}
          >
            <MenuItem value="all">All Representatives</MenuItem>
            <MenuItem value="unassigned">Unassigned</MenuItem>
            {representatives.map((rep) => (
              <MenuItem key={rep._id} value={rep._id}>
                {rep.firstName} {rep.lastName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'companyName'}
                  direction={orderBy === 'companyName' ? order : 'asc'}
                  onClick={() => handleRequestSort('companyName')}
                >
                  <Typography fontWeight="bold">Company Name</Typography>
                </TableSortLabel>
              </TableCell>
              <TableCell><Typography fontWeight="bold">Owner</Typography></TableCell>
              <TableCell><Typography fontWeight="bold">Representative</Typography></TableCell>
              <TableCell><Typography fontWeight="bold">Email</Typography></TableCell>
              <TableCell><Typography fontWeight="bold">Phone</Typography></TableCell>
              <TableCell><Typography fontWeight="bold">Status</Typography></TableCell>
              <TableCell align="center"><Typography fontWeight="bold">Sites</Typography></TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'updatedAt'}
                  direction={orderBy === 'updatedAt' ? order : 'asc'}
                  onClick={() => handleRequestSort('updatedAt')}
                >
                  <Typography fontWeight="bold">Last Modified</Typography>
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedClients.map((client) => (
              <StyledTableRow 
                key={client._id}
                onClick={() => handleViewClient(client)}
                hover
              >
                <TableCell>{client.companyName}</TableCell>
                <TableCell>{client.ownerName}</TableCell>
                <TableCell>
                  {typeof client.representativeId === 'string' 
                    ? 'Not Assigned' 
                    : `${(client.representativeId as unknown as User).firstName} ${(client.representativeId as unknown as User).lastName}`
                  }
                </TableCell>
                <TableCell>{client.email}</TableCell>
                <TableCell>{client.phoneNumber}</TableCell>
                <TableCell>
                  <Chip 
                    label={getStatusLabel(client.pipelineStatus || client.status)}
                    color={getStatusColor(client.pipelineStatus || client.status) as any}
                    size="small"
                  />
                </TableCell>
                <TableCell align="center">{client.properties?.length || 0}</TableCell>
                <TableCell>
                  {client.updatedAt ? new Date(client.updatedAt).toLocaleDateString() : 'N/A'}
                </TableCell>
              </StyledTableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={filteredClients.length}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={[10]}
      />

      <AddClientDialog
        open={isAddClientOpen}
        onClose={handleAddClientClose}
      />
    </Box>
  );
};

export default Clients;
